import { defineComponent, ref, reactive, watch, computed, onBeforeMount } from 'vue'
import { useMainPageStore } from '@/store/mainPage'
import VideoLinkField from '@/features/VideoLinkField/VideoLinkField.vue'
import type {
  IMainPageBlock,
  IMainPageBlockTemplate,
  CreateMainPageBlockPayload,
  UpdateMainPageBlockPayload,
} from '@/types/mainPage'
import type { PropType } from 'vue'
import type { DefaultError } from '@/types/httpError'

export default defineComponent({
  name: 'HomeInfoForm',
  components: {
    VideoLinkField,
  },
  props: {
    block: {
      type: Object as PropType<IMainPageBlock>,
    },
  },
  emits: ['close'],
  setup(props, { emit }) {
    const isEditing = ref(false)
    const blockId = ref<number | ''>('')
    const fields = reactive({
      title: '',
      template: {
        nameRu: '',
        name: '',
      },
      isActive: true,
      // Блок «Видео»: ссылка на ролик VK Видео или Rutube.
      videoUrl: '',
    })
    const isLoading = ref(false)
    const error = ref('')
    const errorFields = ref<Record<string, string[]> | null>(null)
    const mainPageStore = useMainPageStore()

    const templates = computed<IMainPageBlockTemplate[]>(() => mainPageStore.templates ?? [])

    const getTemplate = (value: string) => templates.value.find(i => i.name === value)

    const isVideo = computed(() =>
      isEditing.value ? props.block?.template === 'video' : fields.template.name === 'video',
    )

    watch(
      () => props.block,
      newVal => {
        if (newVal) {
          isEditing.value = true
          const { id, template, ...rest } = newVal
          blockId.value = id

          Object.assign(fields, {
            ...rest,
            canvas: getTemplate(template),
            videoUrl: newVal.videoLink?.url ?? '',
          })
        }
      },
      {
        immediate: true,
      },
    )

    const onValueUpdate = (
      key: keyof typeof fields,
      value: { nameRu: string, name: string } | boolean,
    ) => {
      if (key === 'template' && typeof value !== 'boolean') {
        fields[key] = value
      }
      if (key === 'isActive') {
        fields[key] = value as boolean
      }
    }

    const resetErrors = () => {
      error.value = ''
      errorFields.value = null
      mainPageStore.error = null
    }

    const handleCreateMainPageBlock = async () => {
      isLoading.value = true
      resetErrors()

      const { title, template, isActive, videoUrl } = fields
      const body: CreateMainPageBlockPayload = {
        title,
        template: template.name as CreateMainPageBlockPayload['template'],
        isActive: isActive ? 1 : 0,
        ...(isVideo.value ? { videoUrl: videoUrl.trim() } : {}),
      }

      try {
        await mainPageStore.createBlock(body)
      }
      catch {
        // текст ошибки — в mainPageStore.error, показывается ниже
      }
      isLoading.value = false

      if (mainPageStore.error) {
        error.value = (mainPageStore.error as DefaultError).error
        errorFields.value = (mainPageStore.error as DefaultError).data
        return
      }
      emit('close')
    }

    const handleUpdateMainPageBlock = async () => {
      resetErrors()
      isLoading.value = true
      const { title, isActive, videoUrl } = fields
      const videoUrlChanged = isVideo.value && videoUrl.trim() !== (props.block?.videoLink?.url ?? '')
      const body: UpdateMainPageBlockPayload = {
        id: +blockId.value,
        data: {
          title,
          isActive,
          ...(videoUrlChanged ? { videoUrl: videoUrl.trim() } : {}),
        },
      }

      try {
        await mainPageStore.updateBlock(body)
      }
      catch {
        // текст ошибки — в mainPageStore.error, показывается ниже
      }
      isLoading.value = false

      if (mainPageStore.error) {
        error.value = (mainPageStore.error as DefaultError).error
        errorFields.value = (mainPageStore.error as DefaultError).data
        return
      }
      emit('close')
    }

    const submitHandler = computed(() =>
      isEditing.value ? handleUpdateMainPageBlock : handleCreateMainPageBlock,
    )

    onBeforeMount(async () => {
      if (!isEditing.value) {
        await mainPageStore.getTemplates()
        // Шаблон по умолчанию — первый в списке: его и показывает выбор шаблона.
        if (!fields.template.name && templates.value[0]) {
          fields.template = { ...templates.value[0] }
        }
      }
    })

    return {
      isEditing,
      isVideo,
      fields,
      templates,
      isLoading,
      error,
      errorFields,
      onValueUpdate,
      submitHandler,
    }
  },
})
